import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Truck, Shield, ChevronLeft, ChevronRight } from "lucide-react";
import { Product } from "../types";
import { dataService } from "../services/dataService";
import { formatCurrency } from "../utils/currency";
import ShareButton from "../components/ShareButton";
import ProductCard from "../components/ProductCard";
import PlayfulLoader from "../components/PlayfulLoader";

export const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      if (!productId) return;

      try {
        setIsLoading(true);
        const productResponse = await dataService.getProduct(productId);

        if (productResponse.success && productResponse.data) {
          setProduct(productResponse.data);

          // Get related products (same category, excluding current product)
          if (productResponse.data.categoryId) {
            const relatedProductsResponse = await dataService.getProducts({
              filters: { category: productResponse.data.categoryId },
              limit: 5, // Get 5 to account for excluding current product
            });

            if (
              relatedProductsResponse.success &&
              relatedProductsResponse.data
            ) {
              const related = relatedProductsResponse.data
                .filter((p) => p.id !== productId)
                .slice(0, 4);
              setRelatedProducts(related);
            }
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        setError(
          error instanceof Error ? error.message : "Failed to load product"
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [productId]);

  // These hooks must come before any early returns (rules of hooks)
  const images = React.useMemo(() => {
    if (!product) return [];
    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      return product.images.filter((img) => img && img.trim() !== "");
    }
    if (product.image && product.image.trim() !== "") {
      return [product.image];
    }
    return [];
  }, [product]);

  // Ensure selectedImageIndex is within bounds
  useEffect(() => {
    if (selectedImageIndex >= images.length && images.length > 0) {
      setSelectedImageIndex(0);
    }
  }, [images.length, selectedImageIndex]);

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`h-5 w-5 ${
            i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
        />
      );
    }
    return stars;
  };

  if (isLoading) {
    return (
      <PlayfulLoader
        size="lg"
        variant="stars"
        message="⭐ Loading product details..."
        fullScreen
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to="/products"
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
          >
            Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Product Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The product you're looking for doesn't exist.
          </p>
          <Link
            to="/products"
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
          >
            Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  const handlePreviousImage = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="min-h-screen bg-warm-cream-50">
      {/* Breadcrumbs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link
              to="/"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Home
            </Link>
            <span className="text-gray-400">→</span>
            <Link
              to="/products"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Products
            </Link>
            <span className="text-gray-400">→</span>
            <Link
              to={`/products?category=${product.categoryId}`}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              {product.category}
            </Link>
            <span className="text-gray-400">→</span>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            <div className="relative aspect-square bg-white rounded-xl shadow-lg overflow-hidden group">
              {images.length > 0 ? (
                <>
                  <img
                    src={images[selectedImageIndex]}
                    alt={product.name}
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                  {/* Navigation Arrows */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={handlePreviousImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                      {/* Image Counter */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                        {selectedImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <span className="text-gray-400 text-lg">
                    No image available
                  </span>
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square bg-white rounded-lg shadow-md overflow-hidden transition-all duration-200 ${
                      selectedImageIndex === index
                        ? "ring-2 ring-purple-500 scale-105 shadow-lg"
                        : "hover:scale-105 hover:shadow-lg opacity-75 hover:opacity-100"
                    }`}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Product Name */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {product.name}
              </h1>

              {/* Rating */}
              {product.rating > 0 || product.reviewCount > 0 ? (
                <div className="flex items-center mb-4">
                  <div className="flex items-center mr-3">
                    {renderStars(product.rating)}
                  </div>
                  <span className="text-lg text-gray-600">
                    {product.rating.toFixed(1)}/5 ({product.reviewCount}{" "}
                    reviews)
                  </span>
                </div>
              ) : (
                <div className="mb-4">
                  <span className="text-lg text-gray-400 italic">
                    No ratings yet
                  </span>
                </div>
              )}

              {/* Age Range Badge */}
              {product.ageRange && (
                <div className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <span className="mr-2">👶</span>
                  Ages: {product.ageRange}
                </div>
              )}
            </div>

            {/* Price */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6">
              <div className="flex items-center space-x-4 mb-4">
                {product.price > 0 ? (
                  <>
                    <span className="text-4xl font-bold text-gray-900">
                      {formatCurrency(product.price)}
                    </span>
                    {product.originalPrice &&
                      product.originalPrice > product.price && (
                        <span className="text-2xl text-gray-500 line-through">
                          {formatCurrency(product.originalPrice)}
                        </span>
                      )}
                    {product.onSale && (
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                        SALE!
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-3xl font-semibold text-purple-600">
                    Ask for price
                  </span>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center mb-4">
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    product.inStock
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  <span className="mr-2">{product.inStock ? "✅" : "❌"}</span>
                  {product.inStock
                    ? `In Stock (${product.stockCount} available)`
                    : "Out of Stock"}
                </div>
              </div>

              {/* Share/Order Button */}
              <div className="max-w-sm">
                <ShareButton
                  product={{
                    productName: product.name,
                    price: product.price,
                    productUrl: `${window.location.origin}/product/${product.id}`,
                  }}
                  fullProduct={product}
                  className="w-full"
                />
              </div>
            </div>

            {/* Product Description */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Description
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-4 text-center">
                <Truck className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900 mb-1">
                  Free Shipping
                </h4>
                <p className="text-sm text-gray-600">On orders over ₹2000</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-4 text-center">
                <Shield className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900 mb-1">
                  Quality Guaranteed
                </h4>
                <p className="text-sm text-gray-600">Safe & certified toys</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-4 text-center">
                <Shield className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900 mb-1">
                  No Warranty Policy
                </h4>
                <p className="text-sm text-gray-600">
                  No warranty, no exchange
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Related Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  showShareButton={true}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
